/* ========================================================================
   new-arrivals 専用の元データ取得とキャッシュ制御
   ======================================================================== */
(() => {
  const AuremePlpUtils = window.AuremePlpUtils;
  if (!AuremePlpUtils) return;
  const sharedPlp = window.AuremePlp || {};

  const NEW_ARRIVAL_QUERY_CACHE_LIMIT = 20;

  const {
    ROOT_SELECTOR,
    buildSearchParamsString,
    buildSearchUrl,
    createSearchParams,
    getFilterForm,
    getNamedFieldNames,
    parsePositiveInteger,
    removeSearchParamFields,
    removeSearchParamsByKey,
  } = AuremePlpUtils;

  const newArrivalQueryCache = new Map();

  /* aureme-plp-sales-normalize.js が window.AuremePlp.splitPipeSeparatedValues を先に公開しており、
     new-arrivals ページでは inline 実行で常に利用可能なのでそれを再利用する。 */
  const splitPipeSeparatedValues =
    sharedPlp.splitPipeSeparatedValues ||
    ((value) =>
      value
        ? value
            .split('||')
            .map((entry) => entry.trim())
            .filter(Boolean)
        : []);

  /* Map の挿入順を LRU として利用する。一度 delete してから set し直すことで、対象キーを最新使用位置(末尾)へ移動させる */
  function touchNewArrivalQueryCache(cacheKey, cacheValue) {
    newArrivalQueryCache.delete(cacheKey);
    newArrivalQueryCache.set(cacheKey, cacheValue);
  }

  /* 全ページHTMLを保持するため、古い順に破棄する上限付きキャッシュで運用する */
  function trimNewArrivalQueryCache() {
    while (newArrivalQueryCache.size > NEW_ARRIVAL_QUERY_CACHE_LIMIT) {
      const oldestKey = newArrivalQueryCache.keys().next().value;
      if (!oldestKey) break;
      newArrivalQueryCache.delete(oldestKey);
    }
  }

  function clearNewArrivalQueryCache() {
    newArrivalQueryCache.clear();
  }

  function getNewArrivalVirtualConfig(root = document.querySelector(ROOT_SELECTOR)) {
    if (root?.dataset.auremeNewArrivalVirtualPagination !== 'true') return null;

    return {
      sectionId: root.dataset.sectionId,
      productsPerPage: parsePositiveInteger(root.dataset.auremeNewArrivalProductsPerPage, 20),
      sourcePageCount: parsePositiveInteger(root.dataset.auremeNewArrivalSourcePages, 1),
      paginationLabel: root.dataset.auremeNewArrivalPaginationLabel || 'ページネーション',
      pageLabelTemplate: root.dataset.auremeNewArrivalPageLabelTemplate || '__NUMBER__ページ',
      previousLabel: root.dataset.auremeNewArrivalPreviousLabel || '前のページ',
      nextLabel: root.dataset.auremeNewArrivalNextLabel || '次のページ',
      productCountSimpleTemplate:
        root.dataset.auremeNewArrivalProductCountSimpleTemplate || '__COUNT__個の商品',
      productCountTemplate:
        root.dataset.auremeNewArrivalProductCountTemplate || '__COUNT__個の商品の__PRODUCT_COUNT__',
    };
  }

  function normalizeNewArrivalSearchParams(
    searchParams,
    root = document.querySelector(ROOT_SELECTOR),
  ) {
    /* 販売タイプの正規化 script は条件付き inline のため、未注入時は従来どおり素通しで扱う */
    if (typeof sharedPlp.normalizeNewArrivalSalesSearchParams === 'function') {
      return sharedPlp.normalizeNewArrivalSalesSearchParams(searchParams, root);
    }

    return {
      searchParams: createSearchParams(searchParams),
      didChange: false,
    };
  }

  function buildNewArrivalBaseSearchParams(searchParams, root) {
    const { searchParams: normalizedSearchParams } = normalizeNewArrivalSearchParams(
      searchParams,
      root,
    );

    normalizedSearchParams.delete('page');
    return normalizedSearchParams;
  }

  function removeNewArrivalFilterSearchParams(searchParams) {
    return removeSearchParamsByKey(searchParams, (key) => key.startsWith('filter.'));
  }

  function buildNewArrivalCountBaseSearchParams(root, searchParams) {
    const nextSearchParams = removeNewArrivalFilterSearchParams(searchParams);
    const filterForm = getFilterForm(root);
    if (!filterForm) {
      /* hidden root でも販売タイプ既定値は必要なため、絞り込みフォームが無い場合も new-arrivals 正規化は維持する */
      return buildNewArrivalBaseSearchParams(nextSearchParams, root);
    }

    /* 件数用の母数は「販売タイプ既定値だけを残した検索条件」に揃える。 */
    removeSearchParamFields(nextSearchParams, getNamedFieldNames(filterForm));

    return buildNewArrivalBaseSearchParams(nextSearchParams, root);
  }

  function buildNewArrivalPageSearchParams(searchParams, page) {
    const nextSearchParams = createSearchParams(searchParams);

    if (page > 1) {
      nextSearchParams.set('page', String(page));
    } else {
      nextSearchParams.delete('page');
    }

    return nextSearchParams;
  }

  function buildNewArrivalDatasetUrl(
    searchParams,
    page = null,
    root = document.querySelector(ROOT_SELECTOR),
  ) {
    const datasetSearchParams = buildNewArrivalCountBaseSearchParams(root, searchParams);
    const nextSearchParams =
      page === null
        ? datasetSearchParams
        : buildNewArrivalPageSearchParams(datasetSearchParams, page);

    return buildSearchUrl(nextSearchParams);
  }

  function buildNewArrivalQueryCacheKey(sectionId, searchParams) {
    return `${window.location.pathname}::${sectionId}::${buildSearchParamsString(searchParams)}`;
  }

  async function fetchNewArrivalSectionHtml(sectionId, searchParams) {
    const requestSearchParams = createSearchParams(searchParams);
    requestSearchParams.set('section_id', sectionId);

    const response = await fetch(buildSearchUrl(requestSearchParams));
    if (!response.ok) {
      throw new Error(`Failed to fetch NEW ARRIVALS section: ${response.status}`);
    }

    return response.text();
  }

  function parseNewArrivalVariantFacets(value) {
    if (!value) return [];

    try {
      const parsedValue = JSON.parse(value);
      return Array.isArray(parsedValue) ? parsedValue : [];
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  function buildNewArrivalFacetItem(item) {
    return {
      available: item.dataset.auremeNewArrivalAvailable === 'true',
      tags: splitPipeSeparatedValues(item.dataset.auremeNewArrivalTags),
      variants: parseNewArrivalVariantFacets(item.dataset.auremeNewArrivalVariantFacets),
    };
  }

  function parseNewArrivalEntries(scope) {
    const nextItems = Array.from(scope.querySelectorAll('#product-grid .aureme-plp-grid__item'));
    const entries = [];

    for (const item of nextItems) {
      if (item.dataset.auremeNewArrivalExcluded === 'true') continue;

      entries.push({
        html: item.outerHTML,
        facetItem: buildNewArrivalFacetItem(item),
      });
    }

    return entries;
  }

  function parseNewArrivalSectionResponse(html) {
    const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
    const nextRoot = parsedDocument.querySelector(ROOT_SELECTOR);

    return {
      sourcePageCount: parsePositiveInteger(nextRoot?.dataset.auremeNewArrivalSourcePages, 1),
      entries: parseNewArrivalEntries(parsedDocument),
    };
  }

  function getCurrentNewArrivalSectionResponse(root, currentSearchParams) {
    return {
      sourcePageCount: parsePositiveInteger(root?.dataset.auremeNewArrivalSourcePages, 1),
      currentPage: parsePositiveInteger(createSearchParams(currentSearchParams).get('page'), 1),
      entries: parseNewArrivalEntries(document),
    };
  }

  function canReuseCurrentNewArrivalSection(root, datasetSearchParams, currentSearchParams) {
    return (
      buildSearchParamsString(buildNewArrivalBaseSearchParams(currentSearchParams, root)) ===
      buildSearchParamsString(datasetSearchParams)
    );
  }

  function buildEmptyNewArrivalQueryData() {
    return { sourcePageCount: 1, entries: [], facetItems: [], totalCount: 0 };
  }

  /*
   * 現在表示中の section HTML が同じ母数条件なら fetch 済みページとして再利用する。
   * pageResponses は page 番号を key にし、後段の page 順集約と同じ順序契約へ揃える。
   */
  function reuseCurrentNewArrivalSectionResponse(
    root,
    searchParams,
    currentSearchParams,
    pageResponses,
    sourcePageCount,
  ) {
    if (!canReuseCurrentNewArrivalSection(root, searchParams, currentSearchParams)) {
      return sourcePageCount;
    }

    const currentPageResponse = getCurrentNewArrivalSectionResponse(root, currentSearchParams);
    pageResponses.set(currentPageResponse.currentPage, currentPageResponse);
    return Math.max(sourcePageCount, currentPageResponse.sourcePageCount);
  }

  async function ensureFirstNewArrivalSectionResponse(
    sectionId,
    searchParams,
    pageResponses,
    sourcePageCount,
  ) {
    if (pageResponses.has(1)) {
      return sourcePageCount;
    }

    const firstPageResponse = await fetchNewArrivalSectionResponseForPage(
      sectionId,
      searchParams,
      1,
    );

    pageResponses.set(1, firstPageResponse);
    return Math.max(sourcePageCount, firstPageResponse.sourcePageCount);
  }

  async function fetchNewArrivalSectionResponseForPage(sectionId, searchParams, page) {
    const pageSearchParams = buildNewArrivalPageSearchParams(searchParams, page);
    return parseNewArrivalSectionResponse(
      await fetchNewArrivalSectionHtml(sectionId, pageSearchParams),
    );
  }

  async function fetchRemainingNewArrivalSectionResponses(
    sectionId,
    searchParams,
    pageResponses,
    sourcePageCount,
  ) {
    /*
     * 未取得ページは並列取得するが、pageResponses の key は page 番号にしておく。
     * collectNewArrivalEntries 側で page 順に読み直し、fetch 完了順で商品順が崩れないようにする。
     */
    const remainingPagePromises = [];
    const fetchAndStorePageResponse = async (page) => {
      const response = await fetchNewArrivalSectionResponseForPage(sectionId, searchParams, page);
      pageResponses.set(page, response);
    };

    for (let page = 2; page <= sourcePageCount; page += 1) {
      if (pageResponses.has(page)) continue;

      remainingPagePromises.push(fetchAndStorePageResponse(page));
    }

    await Promise.all(remainingPagePromises);
  }

  function collectNewArrivalEntries(pageResponses, sourcePageCount) {
    const entries = [];

    for (let page = 1; page <= sourcePageCount; page += 1) {
      const pageResponse = pageResponses.get(page);
      if (!pageResponse) continue;
      entries.push(...pageResponse.entries);
    }

    return entries;
  }

  function buildNewArrivalQueryResult(sourcePageCount, entries) {
    return {
      sourcePageCount,
      entries,
      facetItems: entries.map((entry) => entry.facetItem),
      totalCount: entries.length,
    };
  }

  /*
   * new-arrivals は Shopify の各 source page を一度集め、ブラウザ側で facet / 仮想ページングを行う。
   * cache key は「件数母数用に正規化した検索条件」に揃え、page 指定だけで同じ元データを取り直さない。
   */
  function getNewArrivalQueryData(
    root,
    searchParams,
    currentSearchParams = window.location.search,
  ) {
    const config = getNewArrivalVirtualConfig(root);
    if (!config) {
      return Promise.resolve(buildEmptyNewArrivalQueryData());
    }

    const cacheKey = buildNewArrivalQueryCacheKey(config.sectionId, searchParams);
    if (newArrivalQueryCache.has(cacheKey)) {
      const cachedPromise = newArrivalQueryCache.get(cacheKey);
      touchNewArrivalQueryCache(cacheKey, cachedPromise);
      return cachedPromise;
    }

    const requestPromise = (async () => {
      const currentSearchParamsObject = createSearchParams(currentSearchParams);
      const pageResponses = new Map();
      let sourcePageCount = Math.max(config.sourcePageCount, 1);

      /* 既に描画済みのページは再取得せず、そのまま元データの 1 ページとして使い回す */
      sourcePageCount = reuseCurrentNewArrivalSectionResponse(
        root,
        searchParams,
        currentSearchParamsObject,
        pageResponses,
        sourcePageCount,
      );

      sourcePageCount = await ensureFirstNewArrivalSectionResponse(
        config.sectionId,
        searchParams,
        pageResponses,
        sourcePageCount,
      );

      await fetchRemainingNewArrivalSectionResponses(
        config.sectionId,
        searchParams,
        pageResponses,
        sourcePageCount,
      );

      return buildNewArrivalQueryResult(
        sourcePageCount,
        collectNewArrivalEntries(pageResponses, sourcePageCount),
      );
    })();

    touchNewArrivalQueryCache(cacheKey, requestPromise);
    trimNewArrivalQueryCache();
    requestPromise.catch(() => {
      newArrivalQueryCache.delete(cacheKey);
    });

    return requestPromise;
  }

  window.AuremePlpNewArrival = Object.assign(window.AuremePlpNewArrival || {}, {
    buildNewArrivalCountBaseSearchParams,
    buildNewArrivalDatasetUrl,
    buildNewArrivalPageSearchParams,
    clearNewArrivalQueryCache,
    getNewArrivalQueryData,
    getNewArrivalVirtualConfig,
    normalizeNewArrivalSearchParams,
  });
})();
